import React, { useCallback, useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { useParams } from "react-router";
import { Pagination as PaginationType } from "@sscs/types";
import {
  Callout,
  DataTable,
  DataTableColumn,
  FilterBar,
  Page,
  TooltipIcon,
  hasActiveFilters,
} from "@sscs/ui";
import { formatCount, getQueryStatus } from "@sscs/utils";
import { AllowedTo, FetchMoreData, QuotaBar } from "Components";
import { PERMISSION } from "Constants";
import {
  useAppDispatch,
  useAppSelector,
  useCheckPaginationPage,
  useUsageQuota,
} from "Hooks";
import { useGetWhoamiQuery } from "Services";
import FileRow from "../../components/FileRow/FileRow";
import Upload from "../../components/Upload/Upload";
import { useGetFileListQuery } from "../../services/files.api";
import {
  clearFiles,
  filterUpdated,
  initFilter,
  paginationUpdated,
  selectCreatedFiles,
  selectDeletedFiles,
  selectFilesFilter,
  selectFilesPagination,
  selectFilesSort,
  sortUpdated,
} from "../../store/files";
import { FileStreamSortKeys } from "../../types";
import { FILE_STREAM_SORT, transformUser } from "../../utils";
import styles from "./fileStream.module.scss";
import { getFileStreamFilters } from "./Filter";

const SORT_OPTIONS: ReadonlyArray<{
  property: FileStreamSortKeys;
  label: string;
}> = Object.entries(FILE_STREAM_SORT).map(([property, label]) => ({
  property: property as FileStreamSortKeys,
  label,
}));

const COLUMNS: DataTableColumn[] = [
  { id: "info", label: "Info", width: "70px" },
  { id: "status", label: "Status", width: "90px" },
  { id: "approval", label: "Approval", width: "150px" },
  { id: "file", label: "File", width: "100%" },
  { id: "safe", label: "SAFE assessment", width: "400px" },
  { id: "usage", label: "Usage", width: "80px" },
  { id: "spacer", width: "55px" },
];

const FilesView = () => {
  const dispatch = useAppDispatch();
  const { groupId } = useParams();
  const { data: whoami } = useGetWhoamiQuery();
  const { total, used } = useUsageQuota("FILESTREAM");

  const [focusedFileID, setFocusedFileID] = useState<string | null>(null);
  const createdFiles = useAppSelector(selectCreatedFiles);
  const deletedFiles = useAppSelector(selectDeletedFiles);
  const filter = useAppSelector(selectFilesFilter);
  const pagination = useAppSelector(selectFilesPagination);
  const sort = useAppSelector(selectFilesSort);

  // TODO: replace with proper solution for 721 - for some reason refetch nor explicit call to invalidateTags doesn't seem to
  //       affect cache for useGetSingleFileQuery in FileDetails
  const [rerenderKey, setRerenderKey] = useState(0);

  const { data, isLoading, isFetching, isError, error, refetch } =
    useGetFileListQuery(
      groupId && whoami
        ? {
            pagination,
            sort,
            filter: {
              ...filter,
              user: transformUser(filter, whoami.email),
            },
            group_id: groupId,
          }
        : skipToken,
    );

  const deletedSoonCount = data ? data.deleted_soon_count : 0;

  const handlePaginationChange = useCallback(
    (pagination: PaginationType) => {
      dispatch(paginationUpdated(pagination));
    },
    [dispatch],
  );

  useCheckPaginationPage({
    matchCount: data?.match_count,
    itemLength: data?.files.length,
    currentPage: pagination.currentPage,
    itemsPerPage: pagination.itemsPerPage,
    handlePaginationChange,
  });

  return (
    <Page.Root>
      <Page.Header
        title="File stream"
        details={[
          formatCount(data?.total_count ?? 0, "File"),
          deletedSoonCount > 0 && (
            <div key="deletion-warning" className={styles["info-flex"]}>
              <span>
                {formatCount(deletedSoonCount, "file")} will be removed
                within&nbsp;
              </span>
              <Callout type="error" variant="inline" title="7 days" />
              &nbsp;
              <TooltipIcon title="Use the uploads filter to see what will be removed. To keep files, move them to a project." />
            </div>
          ),
        ]}
        actions={
          <div className={styles["quota-and-upload"]}>
            <QuotaBar variant="inline" max={total} value={used} />
            <AllowedTo permissions={[PERMISSION.UPLOAD_FILE]}>
              <Upload />
            </AllowedTo>
          </div>
        }
      />
      <Page.Filters
        id="file-stream-filters"
        found={{
          id: "file-stream-clear-all-filters",
          count: data?.match_count ?? 0,
          initialFilters: initFilter,
          activeFilters: filter,
          onClear: () => dispatch(filterUpdated(initFilter)),
        }}
      >
        <FilterBar
          filters={getFileStreamFilters(filter, (newFilter) => {
            dispatch(filterUpdated(newFilter));
          })}
          sort={{
            id: "file-stream-sort",
            options: SORT_OPTIONS,
            value: sort,
            onChange: (newSort) => dispatch(sortUpdated(newSort)),
          }}
        />
      </Page.Filters>
      <Page.Body>
        <DataTable
          key={rerenderKey}
          size="large"
          columns={COLUMNS}
          empty={{
            subject: "files",
            filters: {
              active: hasActiveFilters(initFilter, filter),
              onReset: () => dispatch(filterUpdated(initFilter)),
            },
          }}
          status={getQueryStatus({
            isLoading,
            isFetching,
            isError,
            error,
            data,
          })}
          pagination={{
            id: "file-stream-pagination",
            itemCount: data?.match_count ?? 0,
            pagination,
            onChange: handlePaginationChange,
          }}
        >
          {[
            ...(createdFiles.length > 0
              ? [
                  <FetchMoreData
                    key="fetch-more"
                    id="show-other-users-files"
                    itemName="file"
                    itemsCount={createdFiles.length}
                    onRefetch={() => {
                      refetch();
                      dispatch(clearFiles());
                      setRerenderKey((prevState) => prevState + 1);
                    }}
                  />,
                ]
              : []),
            ...(data?.files ?? []).map((inputFile, index) => (
              <FileRow
                rowIndex={index}
                key={inputFile.id}
                file={inputFile}
                focused={focusedFileID === inputFile.id}
                deleted={deletedFiles.some((item) => inputFile.id === item)}
                onRowClick={() => setFocusedFileID(inputFile.id)}
              />
            )),
          ]}
        </DataTable>
      </Page.Body>
    </Page.Root>
  );
};

export default FilesView;
